import type { SbBlokData } from "@storyblok/react";
import config from "config/config";
import { DEFAULT_FALLBACK_LANGUAGE, type LanguageCode } from "config/languages";
import { getLocaleFromUrl } from "utils/getLocale";
import getRegularPageUrl from "utils/getPageUrl/getRegularPageUrl";
import { getSlugWithoutLocale } from "utils/getSlugWithoutLocale";
import { getDefaultFolder } from "utils/isDefaultLanguage";
import { getParentLanguage } from "utils/localeUtils";
import type { StoryblokLink } from "../types";
import {
  dynamicLinksPlaceholders,
  dynamicLinksPlaceholdersKeys,
  socialLinksPlaceholders,
  socialLinksPlaceholdersKeys,
} from "./Footer.dynamicLinksConfig";
import type { FooterLinksCategoryType } from "./types";

export const isFooterCategory = (
  component: SbBlokData | FooterLinksCategoryType
): component is FooterLinksCategoryType => component.component === "footerLinksCategory";

const isDynamicLink = (source: string) => dynamicLinksPlaceholdersKeys.includes(source);

const handleDynamicLink = (source: string, language: LanguageCode) =>
  getRegularPageUrl(language, dynamicLinksPlaceholders[source]);

export const isDefaultLink = (source: string) => source.includes("-default");

const handleDefaultLink = (source: string, language: LanguageCode) => {
  const locale = getLocaleFromUrl(source);
  const storySlug = getSlugWithoutLocale(source);

  const isCurrentLanguageDefault = getDefaultFolder(language);

  const isSameDefault = getParentLanguage(locale) === getParentLanguage(language);

  const fallbackLocale = locale === "en-default" ? DEFAULT_FALLBACK_LANGUAGE : getParentLanguage(locale);
  const linkLocale = isCurrentLanguageDefault && isSameDefault ? language : fallbackLocale;

  const storyLink = storySlug ? `/${linkLocale}/${storySlug}` : "";
  const storyUrl = `/${linkLocale}/${getSlugWithoutLocale(source)}`;

  return storyLink || storyUrl;
};

const handleRegularLink = (source: string, linkType: string) => {
  const isInternalLink = linkType === "story";

  if (isInternalLink) return `${config.websiteUrl}/${source}`;

  return source;
};

// TODO: remove this when we have a proper way to get the social links
const isSocialLink = (source: string) => socialLinksPlaceholdersKeys.includes(source);
const handleSocialLink = (source: string, language: LanguageCode) => socialLinksPlaceholders[source](language);

export const resolveLinkSource = (link: StoryblokLink, language: LanguageCode) => {
  const linkSource = link?.cached_url || link?.url;
  if (!linkSource) return "";

  if (isDynamicLink(linkSource)) return handleDynamicLink(linkSource, language);

  if (isDefaultLink(linkSource)) return handleDefaultLink(linkSource, language);

  if (isSocialLink(linkSource)) return handleSocialLink(linkSource, language);

  return handleRegularLink(linkSource, link.linktype);
};

export const isEditMode = (path: string, language: LanguageCode) =>
  path.includes("&_storyblok_c=globalTemplate") || path === `/${language}/global/footer/`;
